import { EventType } from "@/analytics/analytics-events";
import {
  CancelIpxOrderProperties,
  CompleteIpxOrderProperties,
  StartIpxOrderProperties,
} from "@/analytics/ipx-order-events-properties";
import { getMode } from "@/modes";
import { IPX_ORDER_STATUS_URL } from "@/modes/ipx-order-mode/ipx-config";
import { IpxDeliverable } from "@/modes/ipx-order-mode/ipx-embed-messages";
import { isWorkflowMode } from "@/modes/mode";
import { useScansExportStore } from "@/modes/export-mode/scans-export/scans-export-store";
import { Analytics } from "@faro-lotv/foreign-observers";
import { generateGUID } from "@faro-lotv/foundation";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { addBackgroundTask } from "../background-tasks/background-tasks-slice";
import { selectClippingBox } from "../clipping-box-selectors";
import { setClippingBox } from "../clipping-box-slice";
import { selectModeName } from "../mode-selectors";
import { changeMode } from "../mode-slice";
import { selectActiveElementId } from "../selections-selectors";
import { setActiveElement } from "../selections-slice";
import { AppThunk } from "../store";
import {
  selectIpxEstimatedRegionBytes,
  selectIpxOrderMode,
} from "./ipx-order-mode-selectors";
import {
  IpxOrderEntryPoint,
  prepareIpxOrder,
  resetIpxOrder,
} from "./ipx-order-mode-slice";

const DELIVERABLE_LABELS: Record<IpxDeliverable, string> = {
  aiFloorPlan: "AI-generated floor plan",
  floorPlan: "Construction-grade floor plan",
  bimModel: "BIM model",
};

/**
 * @param deliverables The ordered deliverables
 * @returns a human readable list of the deliverables
 */
export function formatIpxDeliverables(deliverables: IpxDeliverable[]): string {
  return deliverables.map((d) => DELIVERABLE_LABELS[d]).join(" and ");
}

/**
 * Starts the IPX Scan-to-BIM ordering wizard
 *
 * @param entryPoint Where the user started the order from
 * @returns a thunk switching to the wizard mode
 */
export function startIpxOrderWizard(entryPoint: IpxOrderEntryPoint): AppThunk {
  return (dispatch, getState) => {
    const state = getState();
    const currentMode = selectModeName(state);
    Analytics.track<StartIpxOrderProperties>(EventType.startIpxOrder, {
      entryPoint,
    });
    dispatch(
      prepareIpxOrder({
        entryPoint,
        returnMode:
          currentMode === "start" || isWorkflowMode(getMode(currentMode))
            ? "overview"
            : currentMode,
        returnClippingBox: selectClippingBox(state),
        returnActiveElementId: selectActiveElementId(state),
      }),
    );
    useScansExportStore.getState().clearSelection();
    dispatch(changeMode("ipxOrder"));
  };
}

/**
 * Leaves the wizard, restoring the mode and clipping box the user had before
 *
 * @returns a thunk closing the wizard
 */
function closeIpxOrderWizard(): AppThunk {
  return (dispatch, getState) => {
    const { returnMode, returnClippingBox, returnActiveElementId } =
      selectIpxOrderMode(getState());
    useScansExportStore.getState().clearSelection();
    dispatch(setClippingBox(returnClippingBox));
    if (returnActiveElementId) {
      dispatch(setActiveElement(returnActiveElementId));
    }
    dispatch(changeMode(returnMode));
    dispatch(resetIpxOrder());
  };
}

/**
 * Leaves the wizard without placing an order
 *
 * @param step Key of the step the user left from
 * @returns a thunk closing the wizard
 */
export function cancelIpxOrderWizard(step: string): AppThunk {
  return (dispatch, getState) => {
    if (!selectIpxOrderMode(getState()).order) {
      Analytics.track<CancelIpxOrderProperties>(EventType.cancelIpxOrder, {
        step,
      });
    }
    dispatch(closeIpxOrderWizard());
  };
}

/**
 * Tracks the placed order in the cloud activity and closes the wizard
 *
 * @returns a thunk completing the wizard
 */
export function completeIpxOrderWizard(): AppThunk {
  return (dispatch, getState) => {
    const state = getState();
    const { order, pointCloudId, sentScans } = selectIpxOrderMode(state);
    if (!order || !pointCloudId) {
      throw new Error("The IPX order has not been placed yet");
    }

    const deliverables = formatIpxDeliverables(order.deliverables);
    Analytics.track<CompleteIpxOrderProperties>(EventType.completeIpxOrder, {
      sizeBytes: selectIpxEstimatedRegionBytes(state) ?? 0,
      scanCount: sentScans.length,
      price: order.price,
      currency: order.currency,
      deliverables: order.deliverables.join(","),
    });

    const now = new Date().toISOString();
    const statusUrl = `${IPX_ORDER_STATUS_URL}?orderId=${encodeURIComponent(order.orderId)}`;
    dispatch(
      addBackgroundTask({
        id: generateGUID(),
        type: "IpxOrder",
        createdAt: now,
        changedAt: now,
        iElementId: pointCloudId,
        devMessage: null,
        state: BackgroundTaskState.started,
        progress: 0,
        metadata: { orderId: order.orderId, deliverables, statusUrl },
      }),
    );
    dispatch(closeIpxOrderWizard());
  };
}
